import { logger } from "../../utils/logger";
import { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { getApiUrl } from '../../utils/apiConfig';

interface BundleSelectorWithGuideProps {
  onRecommendation: (type: '3D' | '2D', tier: 'Starter' | 'Growth' | 'Impact') => void;
}

interface SelectorQuestion {
  label: string;
  options: string[];
}

interface ComparisonRow {
  useCase: string;
  threeDScore: number;
  twoDScore: number;
}

interface BundlesSelectorData {
  badge: string;
  heading: string;
  subheading: string;
  quickSelector: {
    title: string;
    subtitle: string;
    questions: SelectorQuestion[];
    ctaText: string;
    bottomNote: string;
    visible: boolean;
  };
  decisionGuide: {
    title: string;
    subtitle: string;
    comparisonRows: ComparisonRow[];
    proTip: string;
    visible: boolean;
  };
  visible: boolean;
}

// 🔥 HARDCODED FALLBACK DATA (used if API fails)
const hardcodedData: BundlesSelectorData = {
  badge: 'Find Your Perfect Bundle',
  heading: 'Which Bundle Is Right for You?',
  subheading: 'Not sure where to start? Use our quick selector or compare the differences below',
  quickSelector: {
    title: 'Quick Selector',
    subtitle: 'Answer 3 quick questions',
    questions: [
      {
        label: 'Video Type',
        options: ['3D Animation (Complex Products)', '2D Animation (Simple Explainers)']
      },
      {
        label: 'How many videos?',
        options: ['5 videos (Starter)', '10 videos (Growth)', '20 videos (Impact)']
      },
      {
        label: 'Your industry',
        options: ['Manufacturing', 'Energy', 'Automotive', 'Other']
      }
    ],
    ctaText: 'See My Recommendation',
    bottomNote: '💡 Already know what you want? Scroll to pricing below',
    visible: true
  },
  decisionGuide: {
    title: 'Which Style Is Right?',
    subtitle: 'Compare at a glance',
    comparisonRows: [
      { useCase: 'Product Demos', threeDScore: 5, twoDScore: 3 },
      { useCase: 'Training Videos', threeDScore: 5, twoDScore: 4 },
      { useCase: 'Social Media', threeDScore: 3, twoDScore: 5 },
      { useCase: 'Quick & Budget', threeDScore: 2, twoDScore: 5 }
    ],
    proTip: '💡 Pro Tip: Most clients use BOTH — 3D for products, 2D for training',
    visible: true
  },
  visible: true
};

export function BundleSelectorWithGuide({ onRecommendation }: BundleSelectorWithGuideProps) {
  const [selectorData, setSelectorData] = useState<BundlesSelectorData>(hardcodedData);
  const [loading, setLoading] = useState(true);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({
    0: 0, // First question - default to first option
    1: 1, // Second question - default to second option (Growth/10 videos)
    2: 0  // Third question - default to first option
  });

  // ✅ Fetch selector data from CMS on mount
  useEffect(() => {
    const fetchData = async () => {
      try {

        const response = await fetch(getApiUrl('/bundles/selector'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Cache-Control': 'no-cache',
          },
        });

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            // ✅ SAFETY CHECK: Merge with hardcoded defaults to prevent undefined errors
            const safeData: BundlesSelectorData = {
              ...hardcodedData,
              ...result.data,
              quickSelector: {
                ...hardcodedData.quickSelector,
                ...(result.data.quickSelector || {}),
                questions: result.data.quickSelector?.questions || hardcodedData.quickSelector.questions,
              },
              decisionGuide: {
                ...hardcodedData.decisionGuide,
                ...(result.data.decisionGuide || {}),
                comparisonRows: result.data.decisionGuide?.comparisonRows || hardcodedData.decisionGuide.comparisonRows,
              },
            };

            setSelectorData(safeData);
          } else {
            logger.warn('[BundleSelector] ⚠️ API returned no data, using hardcoded fallback');
            setSelectorData(hardcodedData);
          }
        } else {
          logger.warn('[BundleSelector] ⚠️ API request failed, using hardcoded fallback');
          setSelectorData(hardcodedData);
        }
      } catch (error) {
        logger.error('[BundleSelector] ❌ Error fetching data, using hardcoded fallback:', error);
        setSelectorData(hardcodedData);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFindBundle = () => {
    // Get selected answers
    const videoTypeAnswer = selectorData.quickSelector.questions[0]?.options[selectedAnswers[0]] || '';
    const videoCountAnswer = selectorData.quickSelector.questions[1]?.options[selectedAnswers[1]] || '';
    
    // Determine type (3D or 2D) from first question
    const type: '3D' | '2D' = videoTypeAnswer.toLowerCase().includes('3d') ? '3D' : '2D';
    
    // Determine tier from second question
    let tier: 'Starter' | 'Growth' | 'Impact' = 'Growth';
    if (videoCountAnswer.toLowerCase().includes('5') || videoCountAnswer.toLowerCase().includes('starter')) {
      tier = 'Starter';
    } else if (videoCountAnswer.toLowerCase().includes('10') || videoCountAnswer.toLowerCase().includes('growth')) {
      tier = 'Growth';
    } else if (videoCountAnswer.toLowerCase().includes('20') || videoCountAnswer.toLowerCase().includes('impact')) {
      tier = 'Impact';
    }

    onRecommendation(type, tier);
  };

  // ✅ Loading state with spinner
  if (loading) {
    return (
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-16 h-16 border-4 border-[#2F2E5C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading bundle selector...</p>
        </div>
      </section>
    );
  }

  // ✅ Don't render if section is hidden
  if (!selectorData.visible) {
    return null;
  }

  // ✅ Safety check: Ensure nested objects exist before rendering
  const quickSelectorVisible = selectorData.quickSelector?.visible !== false;
  const decisionGuideVisible = selectorData.decisionGuide?.visible !== false;
  const hasQuestions = selectorData.quickSelector?.questions?.length > 0;
  const hasComparisonRows = selectorData.decisionGuide?.comparisonRows?.length > 0;

  // Don't render if both sections are hidden or empty
  if ((!quickSelectorVisible || !hasQuestions) && (!decisionGuideVisible || !hasComparisonRows)) {
    return null;
  }

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#E87D3E] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#2F2E5C] to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#E87D3E]/10 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold text-[#E87D3E]">{selectorData.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2F2E5C] mb-4">
            {selectorData.heading}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {selectorData.subheading}
          </p>
        </div>

        {/* Side-by-Side Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* LEFT: Bundle Selector */}
          {quickSelectorVisible && hasQuestions && (
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C]/5 to-[#E87D3E]/5 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative bg-white rounded-3xl border-2 border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#2F2E5C] mb-2">{selectorData.quickSelector.title}</h3>
                  <p className="text-sm text-gray-600">{selectorData.quickSelector.subtitle}</p>
                </div>

                {/* Selector Form */}
                <div className="space-y-5">
                  {/* Render questions dynamically */}
                  {selectorData.quickSelector.questions.map((question, qIndex) => (
                    <div key={qIndex}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {question.label}
                      </label>
                      <div className="relative">
                        <select
                          value={selectedAnswers[qIndex] || 0}
                          onChange={(e) => setSelectedAnswers({ ...selectedAnswers, [qIndex]: parseInt(e.target.value) })}
                          className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3.5 pr-10 text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#E87D3E] focus:border-transparent transition-all cursor-pointer hover:border-gray-300"
                        >
                          {question.options.map((option, oIndex) => (
                            <option key={oIndex} value={oIndex}>{option}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  ))}

                  {/* CTA Button */}
                  <Button
                    onClick={handleFindBundle}
                    className="w-full h-12 bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#D74444] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all mt-6"
                  >
                    {selectorData.quickSelector.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>

                {/* Optional Note */}
                <p className="text-xs text-center text-gray-500 mt-6">
                  {selectorData.quickSelector.bottomNote}
                </p>
              </div>
            </div>
          )}

          {/* RIGHT: Decision Guide */}
          {decisionGuideVisible && hasComparisonRows && (
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D74444]/5 to-[#2F2E5C]/5 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative bg-white rounded-3xl border-2 border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all h-full flex flex-col">
                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#2F2E5C] mb-2">{selectorData.decisionGuide.title}</h3>
                  <p className="text-sm text-gray-600">{selectorData.decisionGuide.subtitle}</p>
                </div>

                {/* Comparison Table */}
                <div className="flex-1">
                  <div className="overflow-hidden rounded-2xl border-2 border-gray-200">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-[#2F2E5C] to-[#4a487a]">
                          <th className="text-left py-4 px-4 sm:px-6 text-white font-bold text-sm sm:text-base">Use Case</th>
                          <th className="text-center py-4 px-3 sm:px-4 text-white font-bold text-sm sm:text-base">3D</th>
                          <th className="text-center py-4 px-3 sm:px-4 text-white font-bold text-sm sm:text-base">2D</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectorData.decisionGuide.comparisonRows.map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="py-4 px-4 sm:px-6 font-medium text-gray-900 text-sm sm:text-base">{row.useCase}</td>
                            <td className="py-4 px-3 sm:px-4 text-center">
                              <div className="flex justify-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <div
                                    key={i}
                                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                                      i < row.threeDScore
                                        ? 'bg-gradient-to-br from-[#2F2E5C] to-[#4a487a]'
                                        : 'bg-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="py-4 px-3 sm:px-4 text-center">
                              <div className="flex justify-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <div
                                    key={i}
                                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                                      i < row.twoDScore
                                        ? 'bg-gradient-to-br from-[#D74444] to-[#E87D3E]'
                                        : 'bg-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pro Tip */}
                <div className="mt-6 bg-gradient-to-br from-[#E87D3E]/10 to-[#E8B84D]/10 rounded-2xl p-4 border-2 border-[#E87D3E]/20">
                  <p className="text-sm text-gray-700 text-center font-medium">
                    {selectorData.decisionGuide.proTip}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
